import { DATE_FORMAT } from '@/helpers/dates';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetPayeeByExternalIdQuery, useGetProjectByExternalIdQuery } from '@/services/payouts';
import {
  Box,
  DateInput,
  FormikCheckboxInput,
  FormikSelectInputNative,
  FormikTextInput,
  FormLabel,
} from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query';
import { Field, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';

import {
  clawbackCategoryOptions,
  CreateProjectTransactionPayload,
  PayeeProjectRole,
  PayeeType,
  PayoutEvent,
} from 'types';

const EPC_PAYOUT_EVENTS = [
  { value: PayoutEvent.noticeToProceed, label: 'NTP' },
  { value: PayoutEvent.noticeToProceedPlus, label: 'NTP+' },
  { value: PayoutEvent.installSubmitted, label: 'Install Submitted' },
  { value: PayoutEvent.installApproved, label: 'Install Approved' },
  { value: PayoutEvent.ptoApproved, label: 'PTO Approved' },
  { value: PayoutEvent.activationApproved, label: 'Activation Approved' },
  { value: PayoutEvent.warranty, label: 'Warranty' },
  { value: PayoutEvent.invoice, label: 'Materials Invoice' },
  { value: PayoutEvent.standardHoldback, label: 'Standard Holdback' },
  { value: PayoutEvent.domesticContentHoldback, label: 'DC Holdback' },
  { value: PayoutEvent.propertyType, label: 'Property Type' },
  { value: PayoutEvent.equipment, label: 'Equipment' },
  { value: PayoutEvent.shipping, label: 'Shipping' },
  { value: PayoutEvent.loopFieldApproved, label: 'Loop Field Approved' },
];

const MATERIALS_PAYOUT_EVENTS = [
  { value: PayoutEvent.invoice, label: 'Materials Invoice' },
  { value: PayoutEvent.estimate, label: 'Materials Estimate' },
];

export const AddProjectTransactionForm = ({
  payeeType,
  hideAccountSelection,
  allowHistoricalAdjustment = false,
  showFincoEPCOption = false,
}: {
  payeeType?: PayeeType;
  hideAccountSelection?: boolean;
  allowHistoricalAdjustment?: boolean;
  showFincoEPCOption?: boolean;
}) => {
  const { enablePropertyTypeTransactions = false } = useFlags();
  const now = new Date();
  const { values, touched, errors, setFieldValue, isSubmitting } =
    useFormikContext<CreateProjectTransactionPayload>();
  const { projectExternalId: accountId } = values;
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(accountId || skipToken);
  const { data: project, isLoading: isProjectLoading } = useGetProjectByExternalIdQuery(accountId || skipToken);
  const { data: fincoEPC, isLoading: isFincoEPCLoading } = useGetPayeeByExternalIdQuery(
    showFincoEPCOption ? 'light-epc' : skipToken,
  );
  const primaryApplicant = account?.applicants.find((applicant: any) => applicant.type === 'primary');
  const isLoading = isAccountLoading || isProjectLoading;

  const payoutEventOptions = useMemo(() => {
    if (isLoading || !values.payeeId) return [];

    const payeeName = project?.payees?.find((p) => p.id.toString() === values.payeeId);

    if (payeeName?.role === PayeeProjectRole.materials) {
      return MATERIALS_PAYOUT_EVENTS;
    }

    const epcEvents = enablePropertyTypeTransactions
      ? EPC_PAYOUT_EVENTS
      : EPC_PAYOUT_EVENTS.filter((event) => event.value !== PayoutEvent.propertyType);

    return epcEvents;
  }, [isLoading, project?.payees, values.payeeId, enablePropertyTypeTransactions]);

  const payeeOptions = useMemo(
    () => [
      ...(!isProjectLoading && project?.payees
        ? (project?.payees ?? []).map((event: any) => ({
            value: event.id,
            label: event.name,
          }))
        : []),
      ...(!isFincoEPCLoading && fincoEPC ? [{ value: fincoEPC.id, label: fincoEPC.name }] : []),
    ],
    [isProjectLoading, project?.payees, isFincoEPCLoading, fincoEPC],
  );

  const payee = useMemo(() => {
    if (payeeType) {
      const payeeRole = payeeType === PayeeType.organization ? PayeeProjectRole.primary : PayeeProjectRole.materials;
      return project?.payees?.find((payee) => payee.role === payeeRole) || null;
    }
  }, [payeeType, project?.payees]);

  const handleDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (!date || !(date instanceof Date)) return;
      setFieldValue('eventDate', DateTime.fromJSDate(date).toLocal().toFormat(DATE_FORMAT));
    },
    [setFieldValue],
  );
  useEffect(() => {
    setFieldValue('payeeId', payee?.id);
  }, [payee?.id, setFieldValue]);

  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      {!hideAccountSelection && (
        <Box fontSize="sm" padding="md 0" childGap="2xs">
          {primaryApplicant && account && (
            <>
              <Box fontWeight="medium" fontSize="md">
                {account.primaryApplicantName}
              </Box>
              <Box>{primaryApplicant.address.address1}</Box>
              <Box>
                {primaryApplicant.address.city}, {primaryApplicant.address.state} {primaryApplicant.address.zip}
              </Box>
              <Box padding="sm 0 0 0">{primaryApplicant.email}</Box>
            </>
          )}
        </Box>
      )}

      {payee ? (
        <Box childGap={{ base: 'xs', desktop: 'xs' }}>
          <FormLabel inputId="payee">Payee</FormLabel>
          <Box>{payee.name}</Box>
          <br />
        </Box>
      ) : (
        <Field
          label="Payee"
          name="payeeId"
          id="payeeId"
          component={FormikSelectInputNative}
          isRequired
          options={payeeOptions}
        />
      )}

      <Field
        label="Payout Event"
        name="event"
        id="event"
        component={FormikSelectInputNative}
        isRequired
        options={payoutEventOptions}
      />
      {allowHistoricalAdjustment && (
        <Box>
          <Field
            label="Historical Adjustment"
            name="historicalAdjustment"
            id="historicalAdjustment"
            component={FormikCheckboxInput}
            onChange={(e: any) => setFieldValue('historicalAdjustment', e.target.checked)}
          />
          {values.historicalAdjustment && (
            <DateInput
              label="Transaction Date"
              name="transactionDate"
              id="transactionDate"
              placeholder="Pick a date"
              dateFormat={DATE_FORMAT}
              isDisabled={isSubmitting}
              width="2xl"
              value={values.eventDate}
              error={touched.eventDate && errors.eventDate ? errors.eventDate : undefined}
              datePickerProps={{
                onChange: handleDateChange,
                openToDate: values.eventDate ?? now,
              }}
              textInputProps={{
                onClear: () => setFieldValue('ptoGrantedDate', ''),
              }}
            />
          )}
        </Box>
      )}
      <Field
        type="number"
        name="amount"
        id="amount"
        label="Payout Amount"
        component={FormikTextInput}
        isRequired
        prefix="$"
      />
      <Field
        name="clawbackRuleName"
        id="clawbackRuleName"
        label="Clawback Category"
        component={FormikSelectInputNative}
        options={clawbackCategoryOptions}
      />
      <Field name="description" id="description" label="Reason" component={FormikTextInput} />
    </Box>
  );
};
